import * as React from 'react'
import styled from 'styled-components'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { otaPendingReservationService, utilService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import {
  hasAtLeastOnePerson,
  NUM_OF_PEOPLE_ERROR_MESSAGE,
  toNonNegativeInteger,
} from '../../../../utils/otaPendingReservation'
import { Button, Select, InputText, InputTextArea } from '../../../atoms'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import * as constants from '../../../../static/constants'
import {
  OTA_PENDING_PROCESS_ACTION_OPTIONS,
  CHANNEL_SELLER_OPTIONS,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
} from '../../../../static/constants_ota_pending'
import AgentLayoutIndex from '../_layouts'

declare var window: interfaces.IWindow

interface IProps {
  user: {
    users: IJsonResponse
    meta: interfaces.IPager
  }
}

const AgentOtaPendingReservationNew: React.FC<IProps> = props => {
  const { data: user } = utilService.getDataFromJson(props.user)

  Hooks.SetDocumentTitle('新規手動入力')

  // 初期値
  const [formData, setFormData] = React.useState({
    channel_manager: 'airbnb',
    seller: 'airbnb',
    seller_booking_id: '',
    process_action: 'new',
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    customer_country: '',
    num_of_adults: '0',
    num_of_children: '0',
    num_of_infants: '0',
    tour_product_id: '',
    tour_title: '',
    tour_date: '',
    tour_start_at: '09:00',
    total_price: '0',
    other_requests: '',
  })

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string[]>>({})

  // 開始時刻を時と分に分解
  const [startHour, setStartHour] = React.useState('09')
  const [startMinute, setStartMinute] = React.useState('00')

  // 時刻が変更されたらformDataを更新
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      tour_start_at: `${startHour}:${startMinute}`,
    }))
  }, [startHour, startMinute])

  const handleChange = React.useCallback(
    (field: string) => (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const value = e.target.value
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }))
    },
    []
  )

  const handleSubmit = React.useCallback(async () => {
    if (
      !hasAtLeastOnePerson(
        formData.num_of_adults,
        formData.num_of_children,
        formData.num_of_infants
      )
    ) {
      setErrors({ num_of_people: [NUM_OF_PEOPLE_ERROR_MESSAGE] })
      window.flashMessages.addMessage({ text: '入力内容にエラーがあります', type: 'error' })
      return
    }

    try {
      setIsSubmitting(true)
      setErrors({})

      const {
        ota_pending_reservation,
        flush,
      } = await otaPendingReservationService.createOtaPendingReservation({
        status: 1,
        channel_manager: formData.channel_manager,
        seller: formData.seller,
        seller_booking_id: formData.seller_booking_id,
        process_action: formData.process_action,
        customer_first_name: formData.customer_first_name,
        customer_last_name: formData.customer_last_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_country: formData.customer_country,
        num_of_adults: toNonNegativeInteger(formData.num_of_adults),
        num_of_children: toNonNegativeInteger(formData.num_of_children),
        num_of_infants: toNonNegativeInteger(formData.num_of_infants),
        tour_product_id: formData.tour_product_id,
        tour_title: formData.tour_title,
        tour_date: formData.tour_date,
        tour_start_at: formData.tour_start_at,
        currency: 'JPY',
        total_price: toNonNegativeInteger(formData.total_price),
        other_requests: formData.other_requests,
      })

      if (flush) {
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      }

      // 作成成功したら一覧画面にリダイレクト
      if (ota_pending_reservation?.id) {
        window.location.href = `/agent/ota_pending_reservations/${ota_pending_reservation.id}/edit`
      }
    } catch (error) {
      const err = error as any
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors)
        window.flashMessages.addMessage({
          text: '入力内容にエラーがあります',
          type: 'error',
        })
      } else {
        window.flashMessages.addMessage({
          text: '作成に失敗しました',
          type: 'error',
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [formData])

  const handleBack = React.useCallback(() => {
    window.location.href = '/agent/ota_pending_reservations'
  }, [])

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <AgentLayoutIndex
          hideAgentNav={true}
          title={<Heading>新規手動入力</Heading>}
          model={'ota_pending_reservations'}
          main={
            <FormWrapper>
              {/* ステータス */}
              <Section>
                <SectionTitle>ステータス</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>ステータス</FieldLabel>
                    <ReadOnlyValue>下書き</ReadOnlyValue>
                  </FieldGroup>
                </FieldRow>
              </Section>

              {/* OTA情報 */}
              <Section>
                <SectionTitle>OTA情報</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>
                      チャネルマネージャー<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <Select
                      name="channel_manager"
                      options={CHANNEL_SELLER_OPTIONS}
                      value={formData.channel_manager}
                      onChangeHandler={handleChange('channel_manager')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      販売元<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <Select
                      name="seller"
                      options={CHANNEL_SELLER_OPTIONS}
                      value={formData.seller}
                      onChangeHandler={handleChange('seller')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>OTA予約ID</FieldLabel>
                    <InputText
                      name="seller_booking_id"
                      value={formData.seller_booking_id}
                      onChangeHandler={handleChange('seller_booking_id')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      処理アクション<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <Select
                      name="process_action"
                      options={OTA_PENDING_PROCESS_ACTION_OPTIONS}
                      value={formData.process_action}
                      onChangeHandler={handleChange('process_action')}
                    />
                  </FieldGroup>
                </FieldRow>
              </Section>

              {/* 顧客情報 */}
              <Section>
                <SectionTitle>顧客情報</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>名</FieldLabel>
                    <InputText
                      name="customer_first_name"
                      value={formData.customer_first_name}
                      onChangeHandler={handleChange('customer_first_name')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>姓</FieldLabel>
                    <InputText
                      name="customer_last_name"
                      value={formData.customer_last_name}
                      onChangeHandler={handleChange('customer_last_name')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>メールアドレス</FieldLabel>
                    <InputText
                      name="customer_email"
                      value={formData.customer_email}
                      onChangeHandler={handleChange('customer_email')}
                    />
                    {errors.customer_email && (
                      <ErrorText>{errors.customer_email.join(', ')}</ErrorText>
                    )}
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>電話番号</FieldLabel>
                    <InputText
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChangeHandler={handleChange('customer_phone')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>国</FieldLabel>
                    <InputText
                      name="customer_country"
                      value={formData.customer_country}
                      onChangeHandler={handleChange('customer_country')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>大人</FieldLabel>
                    <InputText
                      name="num_of_adults"
                      inputMode="numeric"
                      value={formData.num_of_adults}
                      onChangeHandler={handleChange('num_of_adults')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>子供</FieldLabel>
                    <InputText
                      name="num_of_children"
                      inputMode="numeric"
                      value={formData.num_of_children}
                      onChangeHandler={handleChange('num_of_children')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>幼児</FieldLabel>
                    <InputText
                      name="num_of_infants"
                      inputMode="numeric"
                      value={formData.num_of_infants}
                      onChangeHandler={handleChange('num_of_infants')}
                    />
                  </FieldGroup>
                </FieldRow>
                {errors.num_of_people && <ErrorText>{errors.num_of_people.join(', ')}</ErrorText>}
              </Section>

              {/* ツアー情報 */}
              <Section>
                <SectionTitle>ツアー情報</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>
                      商品ID<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <InputText
                      name="tour_product_id"
                      value={formData.tour_product_id}
                      onChangeHandler={handleChange('tour_product_id')}
                      placeholder="21490P..."
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      OTAツアータイトル<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <InputText
                      name="tour_title"
                      value={formData.tour_title}
                      onChangeHandler={handleChange('tour_title')}
                      placeholder="Make your own sushi at Tsukiji Fish Market"
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>
                      ツアー日<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <InputText
                      name="tour_date"
                      type="date"
                      value={formData.tour_date}
                      onChangeHandler={handleChange('tour_date')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      開始時刻<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <TimeSelectRow>
                      <Select
                        name="start_hour"
                        options={HOUR_OPTIONS}
                        value={startHour}
                        onChangeHandler={e => setStartHour(e.target.value)}
                      />
                      <TimeColon>:</TimeColon>
                      <Select
                        name="start_minute"
                        options={MINUTE_OPTIONS}
                        value={startMinute}
                        onChangeHandler={e => setStartMinute(e.target.value)}
                      />
                    </TimeSelectRow>
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>通貨</FieldLabel>
                    <ReadOnlyValue>JPY</ReadOnlyValue>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>合計金額</FieldLabel>
                    <InputText
                      name="total_price"
                      inputMode="numeric"
                      value={formData.total_price}
                      onChangeHandler={handleChange('total_price')}
                    />
                  </FieldGroup>
                </FieldRow>
              </Section>

              {/* その他情報 */}
              <Section>
                <SectionTitle>その他情報</SectionTitle>
                <FieldRow>
                  <FieldGroup full={true}>
                    <FieldLabel>Other Requests</FieldLabel>
                    <InputTextArea
                      name="other_requests"
                      value={formData.other_requests}
                      onChangeHandler={handleChange('other_requests')}
                    />
                  </FieldGroup>
                </FieldRow>
              </Section>

              {/* アクションボタン */}
              <ActionSection>
                {errors.base && <ErrorText>{errors.base.join(', ')}</ErrorText>}
                <ButtonGroup>
                  <Button handleClick={handleBack}>戻る</Button>
                  <Button primary={true} handleClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? '作成中...' : '作成'}
                  </Button>
                </ButtonGroup>
              </ActionSection>
            </FormWrapper>
          }
        />
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .Index_Main {
    padding: 0 !important;

    .Panel {
      padding: 20px 40px !important;
      overflow: visible;
    }
  }
`

const Heading = styled.div`
  display: flex;
  align-items: center;
`

const FormWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
`

const Section = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background-color: ${constants.COLORS.white};
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 8px;
`

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: bold;
  color: ${constants.COLORS.black};
  border-bottom: 2px solid ${constants.COLORS.primary};
`

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`

const FieldGroup = styled.div<{ full?: boolean }>`
  flex: ${({ full }) => (full ? '1 1 100%' : '1 1 0')};
  min-width: 0;
`

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: bold;
  color: ${constants.COLORS.subText};
`

const RequiredMark = styled.span`
  margin-left: 6px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: normal;
  color: ${constants.COLORS.white};
  background-color: ${constants.COLORS.danger};
  border-radius: 10px;
`

const ReadOnlyValue = styled.div`
  padding: 8px 12px;
  background-color: ${constants.COLORS.background};
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 4px;
  font-size: 14px;
  color: ${constants.COLORS.black};
`

const ErrorText = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${constants.COLORS.danger};
`

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background-color: ${constants.COLORS.white};
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 8px;
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  button {
    min-width: 120px;
  }
`

const TimeSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TimeColon = styled.span`
  font-size: 16px;
  font-weight: bold;
`

export default AgentOtaPendingReservationNew
